import { useCallback, useEffect, useState } from 'react';

import * as sentryClient from '@sentry/browser';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';

import { DEFAULT_SENTRY_CONFIG, SENTRY_DSN, SENTRY_PRODUCT, SENTRY_TEAM_NAME } from './constants';
import type { SentryClientConfig } from './types';

export function useSentryClient(
	isSentryDisabled: boolean,
	config: SentryClientConfig = {},
	location: string,
) {
	const [sentryInitialised, setSentryInitialised] = useState(false);
	const { fireEvent } = useAnalyticsEvents();

	const fireAnalyticsEvent = useCallback(
		(reason: string) => {
			fireEvent('operational.initSentry.failed', {
				reason,
				location,
			});
		},
		[fireEvent, location],
	);

	const setupSentry = useCallback(() => {
		if (
			!sentryInitialised &&
			!isSentryDisabled &&
			!sentryClient.getCurrentHub().getClient() &&
			fg('navx-1574-enable-sentry-logs')
		) {
			setupSentryClient(config, fireAnalyticsEvent);
			setSentryInitialised(true);
		}
	}, [sentryInitialised, isSentryDisabled, config, fireAnalyticsEvent]);

	useEffect(() => {
		setupSentry();
	}, [setupSentry]);

	return { sentryInitialised, sentryClient };
}

function setupSentryClient(
	{
		dsn = SENTRY_DSN,
		env,
		product = SENTRY_PRODUCT,
		teamName = SENTRY_TEAM_NAME,
	}: SentryClientConfig,
	fireEvent?: (reason: string) => void,
) {
	const environment = window.location.href.includes('localhost') ? 'dev' : (env ?? 'prod');

	try {
		sentryClient.init({
			...DEFAULT_SENTRY_CONFIG,
			dsn,
			initialScope: {
				tags: {
					renderMode: teamName || 'unknown',
					productBuild:
						// @ts-ignore Jira  - window.BUILD_KEY, confluence - window.__buildInfo
						window.BUILD_KEY || window.__buildInfo?.FRONTEND_VERSION || 'unknown',
					product,
				},
			},
			environment,
		});
	} catch (error) {
		fireEvent && fireEvent(error instanceof Error ? error.message : 'unknown');
	}
}
